/**
 * IO Tracking Requests API client.
 *
 * Wraps GET/POST/PUT calls to /api/io-requests. All mutations return the
 * full IORequestDetail shape — callers can plug it straight into local state.
 *
 * retryApply and checkIOEligibility removed — those endpoints no longer exist
 * in the new tracking route (no apply step, no IO DB lookup).
 * checkShortnameAvailable kept as dead code for reference (endpoint removed).
 */

import apiClient from './client';
import type {
  IORequestSummary,
  IORequestDetail,
  IORequestView,
  IORequestStats,
  IOFormType,
  IOTrackingPayload,
} from '../types/io-request';
import { decodeIORequestDetail, decodeIORequestSummary } from '../utils/decodeApiStrings';

// ── List + detail ──────────────────────────────────────────────────

export interface ListParams {
  view?: IORequestView;
  status?: string;
  stage?: string;
  form_type?: IOFormType;
  q?: string;
  limit?: number;
  offset?: number;
}

export interface ListResponse {
  requests: IORequestSummary[];
  total: number;
  view: string;
  limit: number;
  offset: number;
  has_more: boolean;
}

export async function listIORequests(
  params: ListParams = {},
): Promise<ListResponse> {
  const { data } = await apiClient.get<ListResponse>('/io-requests', { params });
  return {
    ...data,
    requests: (data.requests ?? []).map(decodeIORequestSummary),
  };
}

export async function getIORequest(id: number): Promise<IORequestDetail> {
  const { data } = await apiClient.get<IORequestDetail>(`/io-requests/${id}`);
  return decodeIORequestDetail(data);
}

// ── Create / edit ──────────────────────────────────────────────────

export interface CreateBody {
  /** The fully-filled form payload. Create + submit happen in one step. */
  payload: IOTrackingPayload;
}

export async function createIORequest(body: CreateBody): Promise<IORequestDetail> {
  const { data } = await apiClient.post<IORequestDetail>('/io-requests', body);
  return decodeIORequestDetail(data);
}

export interface EditBody {
  payload?: IOTrackingPayload | Record<string, unknown>;
}

export async function editIORequest(
  id: number,
  body: EditBody,
): Promise<IORequestDetail> {
  const { data } = await apiClient.put<IORequestDetail>(`/io-requests/${id}`, body);
  return decodeIORequestDetail(data);
}

/** No-op compat shim — creation already submits. Kept so any stale callers don't break. */
export async function submitIORequest(id: number): Promise<IORequestDetail> {
  const { data } = await apiClient.post<IORequestDetail>(`/io-requests/${id}/submit`);
  return decodeIORequestDetail(data);
}

export async function retractIORequest(
  id: number,
  comment: string,
): Promise<IORequestDetail> {
  const { data } = await apiClient.post<IORequestDetail>(
    `/io-requests/${id}/retract`,
    { comment },
  );
  return decodeIORequestDetail(data);
}

// ── Manager actions ────────────────────────────────────────────────

export async function managerApprove(
  id: number,
  comment?: string,
): Promise<IORequestDetail> {
  const { data } = await apiClient.post<IORequestDetail>(
    `/io-requests/${id}/manager-approve`,
    { comment },
  );
  return decodeIORequestDetail(data);
}

export async function managerReject(
  id: number,
  comment: string,
): Promise<IORequestDetail> {
  const { data } = await apiClient.post<IORequestDetail>(
    `/io-requests/${id}/manager-reject`,
    { comment },
  );
  return decodeIORequestDetail(data);
}

// ── Finance actions ────────────────────────────────────────────────

export async function financeApprove(
  id: number,
  comment?: string,
): Promise<IORequestDetail> {
  const { data } = await apiClient.post<IORequestDetail>(
    `/io-requests/${id}/finance-approve`,
    { comment },
  );
  return decodeIORequestDetail(data);
}

export async function financeReject(
  id: number,
  comment: string,
): Promise<IORequestDetail> {
  const { data } = await apiClient.post<IORequestDetail>(
    `/io-requests/${id}/finance-reject`,
    { comment },
  );
  return decodeIORequestDetail(data);
}

// retryApply removed — /retry-apply endpoint no longer exists

// ── Misc ───────────────────────────────────────────────────────────

export async function addIORequestComment(
  id: number,
  comment: string,
): Promise<IORequestDetail> {
  const { data } = await apiClient.post<IORequestDetail>(
    `/io-requests/${id}/comments`,
    { comment },
  );
  return decodeIORequestDetail(data);
}

export async function getIORequestStats(): Promise<IORequestStats> {
  const { data } = await apiClient.get<IORequestStats>('/io-requests/stats');
  return data;
}

/**
 * Mark a request's comment thread as read for the current user.
 * Clears the `io_request_comment` Needs Attention item on the next dashboard load.
 * Fire-and-forget — callers should swallow errors.
 */
export async function markRequestRead(id: number): Promise<void> {
  await apiClient.post(`/io-requests/${id}/mark-read`);
}

// checkShortnameAvailable and checkIOEligibility removed — endpoints deleted
